import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import useInfiniteFetch from '../../queries/useInfiniteFetch';
import { EXPANDED_ROWS_LIMIT } from '../../constants/table';
import useDebounce from '../../queries/auth/useDebounce';
import { colors } from '../../theme/palette/darkPalette';
import DevicesChartTagFilter from 'src/pages/devices-dashboard/DevicesChartTagFilter';
import Loader from '../Loader';

const StyledTextField = styled(TextField)({
  '& svg': {
    fill: '#fff',
  },
  '& label.Mui-focused': {
    color: colors.green,
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: colors.green,
    },
  },
});

const FilterAutocomplete = ({
  selectedValue,
  onChangeFilter,
  fieldIndex,
  sectionIndex,
  queryKey,
  fetchFunction,
  expandedRowsLimit = EXPANDED_ROWS_LIMIT,
  itemName,
  source,
  options
}) => {
  const [searchValue, setSearchValue] = useState(selectedValue);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const isInitialSelectedValueSet = useRef(false);
  const enabled = !!fetchFunction
  const { data, lastElementRef, isLoading } = useInfiniteFetch({
    queryKey,
    fetchFunction,
    expandedRowsLimit,
    itemName,
    enabled,
    searchValue: debouncedSearchValue,
    noCacheTime: true,
  });

  useEffect(() => {
    if (selectedValue && !!searchValue && !isInitialSelectedValueSet.current) {
      setSearchValue(selectedValue);
      isInitialSelectedValueSet.current = true;
    }
  }, [itemName, searchValue, selectedValue]);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);

      if (!event.target.value) {
        onChangeFilter({ source, fieldIndex, sectionIndex, fieldValue: '' });
      }
    },
    [setSearchValue, onChangeFilter, fieldIndex, sectionIndex, source],
  );

  const onChnageTagInput = useCallback(
    (tagNames, tagIds) => {
      onChangeFilter({
        source,
        fieldIndex,
        sectionIndex,
        fieldValue: tagIds || [],
      });
    },
    [fieldIndex, onChangeFilter, sectionIndex, source],
  );
  const formattedData = (enabled ? data : options).map(item => ({
    value: item?.name || item,
    label: item?.name || item,
  }));

  const tagOptions = useMemo(() => {
    if (itemName === 'tags' && Array.isArray(selectedValue)) {
      const dataSource = enabled ? data : (options || []);
      return selectedValue.map(id => dataSource.find(tag => tag.id === id)).filter(Boolean) || [];
    } else {
      return [];
    }
  }, [itemName, selectedValue, data, enabled, options]);

  return (
    <>
      {itemName === 'tags' ? (
        isLoading ? (
          <Loader size="46px" />
        ) : (
          <DevicesChartTagFilter
            source={source}
            isHorizontal={true}
            onApplyTagFilter={onChnageTagInput}
            tagOptions={tagOptions}
            enabled={enabled}
            options={options}
          />
        )
      ) : (
        <Autocomplete
          disableClearable
          value={debouncedSearchValue}
          onChange={(event, data) => {
            onChangeFilter({
              source,
              fieldIndex,
              sectionIndex,
              fieldValue: data?.value || '',
            });
          }}
          isOptionEqualToValue={(option, selectedOption) => option?.value === selectedOption?.value}
          options={formattedData}
          loading={isLoading}
          renderInput={params => (
            <StyledTextField
              {...params}
              placeholder="Select"
              value={searchValue}
              onChange={onChangeSearchValue}
              InputLabelProps={{
                sx: {
                  color: '#fff',
                },
              }}
            />
          )}
          ref={lastElementRef}
        />
      )}
    </>
  );
};

export default FilterAutocomplete;
